import React, { useState, useEffect, useContext } from "react";
import ReactEcharts from "echarts-for-react";
import { SizeContext } from "layouts/AppMain";
import { addNumUnit } from "utils/index";

type DataType = {
  name: string;
  value: number;
};

interface SingleBarProps {
  data: DataType[];
  border?: boolean;
}

const getOption = (
  data: DataType[] = [],
  border: boolean,
  s: (n: number, b?: boolean) => number
) => {
  const labelOption = {
    show: true,
    distance: 8,
    position: "right",
    formatter: ({ dataIndex }: { dataIndex: number }) =>
      `{value|${String(addNumUnit(data[dataIndex]?.value)).replace(
        /[^.\d]+/,
        (a) => `}{unit|${a}`
      )}}`,
    textStyle: {
      rich: {
        value: {
          color: "#fff",
          fontSize: s(20),
          fontFamily: "DIN",
        },
        unit: {
          color: "#fff",
          fontSize: s(18),
          fontWeight: "900",
        },
      },
    },
  };

  const option = {
    grid: {
      top: "10%",
      bottom: "10%",
      left: s(64),
      right: s(100),
    },
    xAxis: {
      show: false,
    },
    yAxis: {
      type: "category",
      inverse: true,
      axisLabel: {
        show: true,
        color: "#589cd2",
        fontSize: s(16),
        fontFamily: "FZLanTingHei",
      },
      axisTick: {
        show: false,
      },
      axisLine: {
        show: false,
      },
      data: data.map((d) => d.name),
    },
    series: [
      {
        type: "bar",
        barWidth: s(8),
        itemStyle: {
          color: {
            type: "linear",
            x: 0,
            x1: 1,
            colorStops: [
              {
                offset: 0,
                color: "#79bbff",
              },
              {
                offset: 1,
                color: "#c6e9fa",
              },
            ],
          },
        },
        max: 1,
        data: data.map((d) => d.value),
        z: 1,
        silent: true,
        label: !border ? labelOption : {},
      },
      ...(border
        ? [
            {
              type: "bar",
              barGap: "-120%",
              label: labelOption,
              barWidth: s(12),
              itemStyle: {
                borderColor: "#353ca5",
                borderWidth: 1,
                color: "rgba(102, 102, 102,0)",
              },
              data: data.map(() => Math.max(...data.map((d) => d.value))),
              silent: true,
              z: 2,
            },
          ]
        : []),
    ],
  };

  return option;
};

const SingleBar: React.FC<SingleBarProps> = ({ data, border = false }) => {
  const getSize = useContext(SizeContext);
  const [option, setOption] = useState<any>({});

  useEffect(() => {
    setOption(getOption(data, border, getSize));
  }, [data, border, getSize]);

  return (
    <ReactEcharts
      option={option}
      notMerge={true}
      style={{ width: "100%", height: "100%", position: "absolute" }}
    />
  );
};

export default SingleBar;
